import { z } from 'zod';
import {
  FeeType,
  FeeCalculationBasis,
  FeeAccounting,
  FeeChargingFrequency,
  CoreFeeType,
} from 'further-types/investment';
import isMongoId from '../../helpers/is-mongo-id';
import { withPaginationParamsOptional } from '../../helpers';

export const CreateOrEditCoreFeeTemplateInput = z.object({
  id: z
    .string()
    .refine(isMongoId({ optional: true }))
    .optional(),
  nameOnlyUpdate: z.boolean().optional(),
  firmId: z
    .string()
    .refine(isMongoId({ optional: true }))
    .optional(),
  feeTemplateName: z.string().min(1, 'Fee template name is required'),
  coreFees: z.array(
    z
      .object({
        feeName: z.string().min(1, { message: 'Fee name is required' }),
        feeType: z.nativeEnum(FeeType, {
          message: 'Please select fee type',
        }),
        displayFeeType: z.nativeEnum(CoreFeeType, {
          message: 'Please select display fee type',
        }),
        netFee: z
          .number({
            message: 'Please enter a valid net fee',
          })
          .min(0.01, { message: 'Net fee must be greater than 0' }),
        vat: z.number().refine((val) => val === 0 || val === 20, {
          message: 'Please select VAT (must be 0 or 20)',
        }),
        grossFee: z
          .number()
          .min(0.01, { message: 'Gross fee must be greater than 0' }),
        feeCalculationBasis: z
          .nativeEnum(FeeCalculationBasis)
          .default(FeeCalculationBasis.PercentageOfTotalCommitment)
          .optional()
          .nullable(),
        chargingFrequency: z
          .nativeEnum(FeeChargingFrequency, {
            message: 'Please select a valid charging frequency',
          })
          .optional()
          .nullable(),
        minimumFeePercentage: z
          .number({
            message: 'Please enter a valid minimum fee percentage',
          })
          .optional()
          .nullable(),
        startingPeriod: z
          .number({
            message: 'Please enter a valid starting period',
          })
          .optional()
          .nullable(),
        finishingPeriod: z
          .number({
            message: 'Please enter a valid finishing period',
          })
          .optional()
          .nullable(),
        feeAccounting: z.nativeEnum(FeeAccounting, {
          message: 'Please select fee accounting.',
        }),
      })
      .refine(
        (coreFee) => {
          if (coreFee.feeType === FeeType.Initial) return true;

          if (!coreFee.chargingFrequency) return false;

          return Object.values(FeeChargingFrequency).includes(
            coreFee.chargingFrequency,
          );
        },
        {
          message: 'Please enter a valid charging frequency',
          path: ['chargingFrequency'],
        },
      )
      .refine(
        (coreFee) => {
          if (coreFee.feeType !== FeeType.Recurring) return true;
          return !!coreFee.startingPeriod;
        },
        {
          message: 'Please enter starting period',
          path: ['startingPeriod'],
        },
      )
      .refine(
        (coreFee) => {
          if (coreFee.feeType !== FeeType.Recurring) return true;
          return coreFee.startingPeriod && coreFee.startingPeriod >= 1;
        },
        {
          message: 'Lowest starting period is 1',
          path: ['startingPeriod'],
        },
      )
      .refine(
        (coreFee) => {
          if (coreFee.feeType !== FeeType.Recurring) return true;
          return !!coreFee.finishingPeriod;
        },
        {
          message: 'Please enter finishing period',
          path: ['finishingPeriod'],
        },
      )
      .refine(
        (coreFee) => {
          if (coreFee.feeType !== FeeType.Recurring) return true;
          return coreFee.finishingPeriod && coreFee.finishingPeriod >= 1;
        },
        {
          message: 'Lowest finishing period is 1',
          path: ['finishingPeriod'],
        },
      )
      .refine(
        (coreFee) => {
          if (coreFee.feeType !== FeeType.Recurring) return true;
          if (!coreFee.startingPeriod || !coreFee.finishingPeriod) return true;
          return coreFee.startingPeriod <= coreFee.finishingPeriod;
        },
        {
          message:
            'Please ensure the finishing period is not before the starting period.',
          path: ['finishingPeriod'],
        },
      )
      .refine(
        (coreFee) => {
          if (coreFee.feeType !== FeeType.Initial) return true;
          return !coreFee.chargingFrequency;
        },
        {
          message: 'Charging frequency should not be set for initial fees',
          path: ['chargingFrequency'],
        },
      )
      .refine(
        (coreFee) => {
          if (coreFee.displayFeeType !== CoreFeeType.RecurringPercentage)
            return true;
          return !!coreFee.feeCalculationBasis;
        },
        {
          message: 'Fee calculation basis should be set for recurring % fees',
          path: ['feeCalculationBasis'],
        },
      )
      .refine(
        (coreFee) => {
          if (
            !coreFee.minimumFeePercentage &&
            coreFee.feeCalculationBasis !==
              FeeCalculationBasis.PercentageOfUnrealisedAssetCost
          )
            return true;
          return (
            coreFee.minimumFeePercentage === 0 || !!coreFee.minimumFeePercentage
          );
        },
        {
          message:
            'Minimum fee percentage should only be set for UnrealisedAssetCost type fees',
          path: ['minimumFeePercentage'],
        },
      ),
  ),
});

export const ListCoreFeeTemplatesInput = withPaginationParamsOptional(
  z.object({
    firmId: z
      .string()
      .refine(isMongoId({ optional: true }))
      .optional(),
    name: z.string().optional(),
  }),
  ['createdAt'],
);

export const DeleteCoreFeeTemplateInput = z.object({
  id: z.string().refine(isMongoId()),
  firmId: z
    .string()
    .refine(isMongoId({ optional: true }))
    .optional(),
});

export const GetCoreFeeTemplateByIdInput = z.object({
  id: z.string().refine(isMongoId()),
});
